import type { Entity } from '@backstage/catalog-model';
import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

export interface GitLabJob {
  id: number;
  name: string;
  stage: string;
  status: string;
  ref: string;
  commitSha: string;
  shortCommitSha: string;
  pipelineId: number;
  pipelineWebUrl?: string;
  triggeredBy?: string;
  startedAt?: string;
  durationSeconds?: number;
  webUrl: string;
}

export interface GitLabJobsPage {
  items: GitLabJob[];
  nextCursor?: string;
}

export interface GitLabJobsApi {
  getJobs(options: {
    entityRef: string;
    cursor?: string;
  }): Promise<GitLabJobsPage>;
}

export const gitLabJobsApiRef = createApiRef<GitLabJobsApi>().with({
  id: 'plugin.gitlab-jobs.service',
});

export class GitLabJobsClient implements GitLabJobsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  async getJobs(options: {
    entityRef: string;
    cursor?: string;
  }): Promise<GitLabJobsPage> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('gitlab-jobs');
    const url = new URL(`${baseUrl}/jobs`);
    url.searchParams.set('entityRef', options.entityRef);
    if (options.cursor) {
      url.searchParams.set('cursor', options.cursor);
    }

    const response = await this.#fetchApi.fetch(url);
    if (response.status === 404) {
      throw new Error(
        'GitLab project unavailable. Check its catalog mapping and service-account access.',
      );
    }
    if (response.status === 503) {
      throw new Error('GitLab is unavailable. Try again shortly.');
    }
    if (response.status === 403) {
      throw new Error('You are not permitted to view GitLab Jobs.');
    }
    if (!response.ok) {
      throw new Error('Unable to load GitLab Jobs.');
    }
    return response.json() as Promise<GitLabJobsPage>;
  }
}

export const gitLabJobsApi = ApiBlueprint.make({
  name: 'service',
  params: defineParams =>
    defineParams({
      api: gitLabJobsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new GitLabJobsClient({ discoveryApi, fetchApi }),
    }),
});

const GITLAB_HOST = 'gitlab.neusix.ai';

export function hasGitLabJobs(entity: Entity): boolean {
  if (entity.kind.toLocaleLowerCase('en-US') !== 'component') {
    return false;
  }
  const annotations = entity.metadata.annotations ?? {};
  if (annotations['neusix.ai/gitlab-project-path']) {
    return true;
  }
  return [
    annotations['backstage.io/source-location'],
    annotations['backstage.io/managed-by-location'],
  ].some(location => location?.includes(`://${GITLAB_HOST}/`));
}
